import { computed, inject, Injector } from '@angular/core';
import { catchError, EMPTY, Observable, pipe, tap } from 'rxjs';
import { patchState, signalStore, type, withComputed, withMethods, withState } from '@ngrx/signals';
import {
  entityConfig,
  setAllEntities,
  updateAllEntities,
  updateEntity,
  withEntities,
  prependEntity,
} from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { withCallStatus } from '@ngrx-traits/signals';

import { FormlySelectOption } from '@ngx-formly/core/select';
import { DEFAULT_VISIBLE_OPTIONS_COUNT, excludeOptions, isPeriodOption } from './date.util';

type Option = FormlySelectOption & {
  active?: boolean;
};

type PeriodState = {
  initialVisibleOptionsCount: number;
  visibleOptionsCount: number;
  searchEnabled: boolean;
  search: string;
};

const initialState: PeriodState = {
  initialVisibleOptionsCount: DEFAULT_VISIBLE_OPTIONS_COUNT,
  visibleOptionsCount: DEFAULT_VISIBLE_OPTIONS_COUNT,
  search: '',
  searchEnabled: false,
};

const optionsConfig = entityConfig({ entity: type<Option>(), selectId: o => o.value });

export const DateStore = signalStore(
  withState(initialState),
  withEntities<Option>(optionsConfig),
  withCallStatus({ prop: 'options' }),
  withComputed(({ entities, visibleOptionsCount, search }) => {
    const totalOptionsCount = computed(() => entities().length);

    const filteredOptions = computed(() =>
      entities().filter(o => excludeOptions(o) && o.label?.toLowerCase().includes(search().toLowerCase()))
    );

    const periodOption = computed(() => entities().find(o => isPeriodOption(o)));

    const visibleOptions = computed(() => filteredOptions().slice(0, visibleOptionsCount()));

    const hasMoreOptions = computed(() => filteredOptions().length > visibleOptionsCount());


    return {
      hasMoreOptions,
      totalOptionsCount,
      visibleOptions,
      periodOption,
    };
  }),
  withMethods((
    store,
    injector = inject(Injector),
  ) => {
    const setInitialVisibleOptionsCount = (count: number) => patchState(
      store,
      { visibleOptionsCount: count ?? DEFAULT_VISIBLE_OPTIONS_COUNT }
    );

    const setOptions = (options: Observable<FormlySelectOption[]>, selectedOptions: any | any[]) => rxMethod<Option[]>(
      pipe(
        tap(options => {
          store.setOptionsLoading();

          const opts = Array.isArray(selectedOptions) ? selectedOptions : [selectedOptions];

          patchState(
            store,
            setAllEntities(
              (options ?? []).map(o => ({ ...o, active: o.active ?? opts.includes(o.value) } as Option)),
              optionsConfig
            )
          );

          store.setOptionsLoaded();

          if (!selectedOptions) return;
        }),
        catchError(error => {
          store.setOptionsError(error);
          return EMPTY;
        })
      ),
      { injector }
    )(options);

    const showMoreOptions = () =>
      patchState(
        store,
        state => ({ visibleOptionsCount: state.visibleOptionsCount + DEFAULT_VISIBLE_OPTIONS_COUNT })
      );

    const showLessOptions = () => patchState(
      store,
      state => ({
        visibleOptionsCount: state.initialVisibleOptionsCount || state.visibleOptionsCount || DEFAULT_VISIBLE_OPTIONS_COUNT
      })
    );

    const setPeriodOption = (opt: Option) => {
      const periodOption = store.periodOption();
      patchState(
        store,
        periodOption
          ? updateEntity({ id: periodOption.value, changes: () => opt }, optionsConfig)
          : prependEntity(opt, optionsConfig)
      );
    };

    const toggleOption = (value: any | undefined) => {
      patchState(
        store,
        updateAllEntities(
          (opt: Option) => ({ active: value === opt.value ? true : false }),
          optionsConfig
        )
      );
    };

    const toggleSearchBar = () => patchState(store, state => ({ searchEnabled: !state.searchEnabled }));

    const searchOptions = (input: string) => patchState(store, { search: input });

    return {
      setPeriodOption,
      searchOptions,
      setOptions,
      showLessOptions,
      showMoreOptions,
      toggleOption,
      toggleSearchBar,
      setInitialVisibleOptionsCount,
    };
  }),
);
